import { useState } from "react";
import { SparklesIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface Props {
    /** The banner heading, e.g. "Trainee Performance Insights". */
    title: string;
    /** The insight body (rendered inside the collapsible region). */
    children: React.ReactNode;
    /** Extra classes for the outer container (e.g. spacing per page). */
    className?: string;
}

/**
 * The blue "… Performance Insights" banner shown on the training results and
 * AIQM detail pages. The header is a toggle that collapses/expands the body;
 * it starts expanded so the insight is visible by default.
 */
export function PerformanceInsightsBanner({ title, children, className }: Props) {
    const [open, setOpen] = useState(true);

    return (
        <div
            className={cn(
                "rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3",
                className,
            )}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-2 text-left"
            >
                <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-blue-700">
                    <SparklesIcon className="w-3 h-3" />
                    {title}
                </span>
                {open ? (
                    <ChevronUpIcon className="w-4 h-4 text-blue-700 flex-shrink-0" />
                ) : (
                    <ChevronDownIcon className="w-4 h-4 text-blue-700 flex-shrink-0" />
                )}
            </button>
            {open && (
                <p className="text-sm text-gray-700 leading-relaxed mt-1.5">
                    {children}
                </p>
            )}
        </div>
    );
}
